import posthog from "posthog-js";
import { useEffect, useState } from "react";

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/app/components/ui/sheet";
import { useAuth } from "@/app/context/Auth/AuthContext";

import { AssessmentRecord, FitAnswer, useAnswers, useSaveAnswers } from "../config/api";
import TailorResumeDialog from "../tailored/TailorResumeDialog";
import FitReport from "./FitReport";

interface FitCheckDetailDrawerProps {
	record: AssessmentRecord | null;
	onClose: () => void;
}

function FitCheckDetail({ record, onBack }: { record: AssessmentRecord; onBack: () => void }) {
	const { userData } = useAuth();
	const userId = userData?.userID || "";
	const jobProps = { jobId: record.jobId, jobTitle: record.roleTitle };

	const { data: savedAnswers } = useAnswers(userId, record.jobId);
	const { mutate: saveAnswers } = useSaveAnswers();

	const [answers, setAnswers] = useState<FitAnswer[]>([]);
	const [tailorOpen, setTailorOpen] = useState(false);

	useEffect(() => {
		if (savedAnswers && savedAnswers.length > 0) setAnswers(savedAnswers);
	}, [savedAnswers]);

	const handleComplete = (finalAnswers: FitAnswer[]) => {
		if (userId) saveAnswers({ userId, jobId: record.jobId, answers: finalAnswers });
		posthog.capture("fitcheck_questions_completed", {
			...jobProps,
			total: finalAnswers.length,
			haveCount: finalAnswers.filter((a) => a.answer === "have").length,
			lackCount: finalAnswers.filter((a) => a.answer === "lack").length,
		});
	};

	const handleUpdateCv = () => {
		posthog.capture("tailored_resume_opened", {
			...jobProps,
			haveCount: answers.filter((a) => a.answer === "have").length,
			lackCount: answers.filter((a) => a.answer === "lack").length,
		});
		setTailorOpen(true);
	};

	return (
		<>
			<FitReport
				assessment={record.assessment}
				answers={answers}
				onAnswersChange={setAnswers}
				onComplete={handleComplete}
				onChangeResume={onBack}
				onUpdateCv={handleUpdateCv}
				jobId={record.jobId}
				jobTitle={record.roleTitle}
			/>
			<TailorResumeDialog
				open={tailorOpen}
				onOpenChange={setTailorOpen}
				userId={userId}
				jobId={record.jobId}
				jobDetails={{ _id: record.jobId, jobTitle: record.roleTitle, companyName: record.company }}
				roleTitle={record.roleTitle}
			/>
		</>
	);
}

export const FitCheckDetailDrawer = ({ record, onClose }: FitCheckDetailDrawerProps) => (
	<Sheet
		open={!!record}
		onOpenChange={(open) => {
			if (!open) onClose();
		}}
	>
		<SheetContent side="right" className="w-full gap-0 overflow-y-auto p-0 sm:!max-w-3xl">
			<SheetHeader className="border-b border-gray-200 p-5 pr-14">
				<SheetTitle className="font-plus-jakarta text-lg font-bold text-gray-900">
					{record?.roleTitle || "FitCheck"}
				</SheetTitle>
				<SheetDescription className="text-gray-600">{record?.company || ""}</SheetDescription>
			</SheetHeader>

			{record && <FitCheckDetail key={record.jobId} record={record} onBack={onClose} />}
		</SheetContent>
	</Sheet>
);

export default FitCheckDetailDrawer;
